import { Modal } from "react-bootstrap";
import styles from "./modal.module.css";
import React from "react";
import { Artwork } from "@/types/artworksData";
import { useSelector } from "react-redux";
import { AppState } from "@/store/root.reducer";
import { ApiClient } from "../../utils/apiClient";

const BASE_API = process.env.REACT_APP_BASE_API;

interface ModalProps {
    show: boolean;
    onHide: () => void;
    onSent: (status: boolean, msg?: string) => void;
    artwork: Artwork;
}

export const EnquireModal = ({show, onHide, onSent, artwork}: ModalProps) => {
    const apiClient = ApiClient(BASE_API);
    const initialBody = `Hi. I am interested in the artwork.\nPlease reach out to me.\nThank you`;

    const [ enquiryBody, setEnquiryBody ] = React.useState<string>(initialBody);
    const [ email, setEmail ] = React.useState<string>("");
    const [ agreed, setAgreed ] = React.useState<boolean>(false);
    const [ loading, setLoading ] = React.useState<boolean>(false);
    
    // Server error state management
    const [ serverErrors, setServerErrors ] = React.useState<any>(null);
    
    // Track if fields have been modified to hide errors on typing
    const [ emailTouched, setEmailTouched ] = React.useState<boolean>(false);
    const [ enquiryBodyTouched, setEnquiryBodyTouched ] = React.useState<boolean>(false);

    const user = useSelector((state: AppState) => state.auth.user);

    React.useEffect(() => {
        if (user) {
            setEmail(user.email);
        }
    }, [user]);

    // Clear errors when modal is closed
    React.useEffect(() => {
        if (!show) {
            setServerErrors(null);
            setEmailTouched(false);
            setEnquiryBodyTouched(false);
        }
    }, [show]);

    // Reset touched states when new server errors come in
    React.useEffect(() => {
        if (serverErrors) {
            setEmailTouched(false);
            setEnquiryBodyTouched(false);
        }
    }, [serverErrors]);

    // Parse server errors to field-specific errors (like other components in the app)
    const parseServerErrors = (error: any) => {
        const fieldErrors: { [key: string]: string } = {};
        
        if (!error) return fieldErrors;
        
        console.log("Parsing server errors:", error); // Debug log
        
        // Check if error has field-specific validation errors
        if (typeof error === 'object' && error) {
            // Handle validation errors object
            if (error.errors) {
                Object.keys(error.errors).forEach(field => {
                    const fieldError = error.errors[field];
                    if (typeof fieldError === 'string') {
                        fieldErrors[field] = fieldError;
                    } else if (fieldError.message) {
                        fieldErrors[field] = fieldError.message;
                    } else if (Array.isArray(fieldError)) {
                        fieldErrors[field] = fieldError[0]; // Take first error if array
                    }
                });
            }
            
            // Handle direct field errors for enquire form
            if (error.email) fieldErrors.email = typeof error.email === 'string' ? error.email : error.email.message || 'Invalid email';
            if (error.content) fieldErrors.enquiryBody = typeof error.content === 'string' ? error.content : error.content.message || 'Invalid enquiry message';
            if (error.body) fieldErrors.enquiryBody = typeof error.body === 'string' ? error.body : error.body.message || 'Invalid enquiry message';
            
            // Handle msg field (common in your app)
            if (error.msg && !Object.keys(fieldErrors).length) {
                fieldErrors.general = error.msg;
            }
        }
        
        // If it's a string error, try to parse as JSON
        if (typeof error === 'string') {
            try {
                const parsed = JSON.parse(error);
                return parseServerErrors(parsed);
            } catch {
                // If not JSON, treat as general error
                fieldErrors.general = error;
            }
        }
        
        return fieldErrors;
    };

    // Get field-specific error messages
    const getFieldError = (fieldName: string) => {
        // Parse server errors if they exist
        const parsedErrors = serverErrors ? parseServerErrors(serverErrors) : {};
        
        if (!parsedErrors || Object.keys(parsedErrors).length === 0) return null;
        
        // Hide errors if user has started typing in the field
        if (fieldName === 'email' && emailTouched) return null;
        if (fieldName === 'enquiryBody' && enquiryBodyTouched) return null;
        
        // Return the specific field error if it exists
        const fieldError = parsedErrors[fieldName];
        if (fieldError) {
            return typeof fieldError === 'string' ? fieldError : String(fieldError);
        }
        
        return null;
    };

    // Clear server errors when user starts typing
    const clearFieldError = () => {
        setServerErrors(null);
    };

    // Handle email change with touched state
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setEmailTouched(true);
    };

    // Handle enquiry body change with touched state
    const handleEnquiryBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEnquiryBody(e.target.value);
        setEnquiryBodyTouched(true);
    };

    const onSubmit = async () => {
        setLoading(true);
        setServerErrors(null); // Clear previous errors

        const body = `${enquiryBody}\n\nArtwork: ${artwork.artistName} - ${artwork.title} (${artwork._id})\nCustomer email address: ${email}`;
        const resp = await apiClient.sendEnquire(email, body, artwork._id);
        
        setLoading(false);
        
        if (resp.status) {
            onSent(true);
        } else {
            // Set server errors for inline display
            console.log("Server response error:", resp); // Debug log
            
            // The error data is in resp.data (from error.response.data)
            if (resp.data && typeof resp.data === 'object') {
                setServerErrors(resp.data);
            } else {
                // If only a message is returned, treat it as a general error
                setServerErrors({ general: resp.data || "An error occurred" });
            }
            
            // Extract message for the general notification
            const errorMessage = resp.data?.message || resp.data?.msg || resp.data || "An error occurred";
            onSent(false, errorMessage);
        }
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            backdrop="static"
        >
            <Modal.Header closeButton className={styles.background}>
                <Modal.Title id="contained-modal-title-vcenter">
                    Submit Enquiry
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.background}>
                <div className="p-3">
                    <div className="mb-3">
                        <label htmlFor="enquiryBody">Send an enquiry about to the exhibitor</label>
                        <textarea 
                            className={`form-control ${getFieldError('enquiryBody') ? 'is-invalid' : ''}`}
                            id="enquiryBody"
                            name="enquiryBody" 
                            rows={10} 
                            value={enquiryBody}
                            onChange={handleEnquiryBodyChange}
                        />
                        {getFieldError('enquiryBody') && (
                            <div className="text-danger small mt-1">
                                {getFieldError('enquiryBody')}
                            </div>
                        )}
                    </div>
                    <div className="mb-3 d-flex align-items-center">
                        <label htmlFor="email" className="me-2 w-25">Artwork:</label>
                        <input readOnly className="form-control" type="text" value={`${artwork.artistName} - ${artwork.title} (${artwork._id})`} />
                    </div>
                    <div className="mb-3">
                        <div className="d-flex align-items-center">
                            <label htmlFor="email" className="me-2 w-25">My email address:</label>
                            <input 
                                className={`form-control ${getFieldError('email') ? 'is-invalid' : ''}`}
                                type="email" 
                                id="email"
                                value={email} 
                                onChange={handleEmailChange}
                            />
                        </div>
                        {getFieldError('email') && (
                            <div className="text-danger small mt-1">
                                {getFieldError('email')}
                            </div>
                        )}
                    </div>
                    <div className="d-flex">
                        <input id="agree" className="form-check-inline" type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                        <label htmlFor="agree" className="small">
                            I agree to share my email address and the other contents of this message with the exhibitor of this artwork<span style={{color: 'red'}}>*</span>
                        </label>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className={styles.background}>
                <button className="btn btn-success" onClick={onHide}>
                    Close
                </button>
                <button
                    className="btn btn-success" 
                    style={{pointerEvents: "auto"}}
                    onClick={onSubmit} 
                    disabled={!agreed || loading}
                    title={!agreed ? "Please tick the box before submitting" : ""}
                >
                    Submit
                </button>
            </Modal.Footer>
        </Modal>
    )
}